// Stock (design/screens/stock.html; UC-INVENTORY-001..004): a branch's stock lines in their five states with available, the
// weighted average, the value and the last receipt, a Re-order chip on each line below its level (BR-INVENTORY-022) and a sub-line
// naming what holds units apart from the store; the stats strip over all the branch's lines; the state chips, Below re-order only,
// the category and the search (an SKU, a product, a serial or a barcode, UC-INVENTORY-001 A2) kept in the address; the branch
// picker — the branches the user reads, All branches for an Owner or Admin (A1, BR-INVENTORY-024). Selecting a line shows its
// serials (UC-INVENTORY-004 A8) or its figures, its re-order level (UC-INVENTORY-002) and its latest movements (UC-INVENTORY-003);
// with All branches in view the selection names the line's branch. A product the branch never held is given a re-order level
// from "Set a re-order level…" (UC-INVENTORY-002 A2). A document is linked only for the roles that may open it.
// The page head opens Products, Stock take, Adjust (UC-INVENTORY-006), Transfers and Receive delivery (the goods receipt's
// screen, a later stage: a placeholder says when). Stock is changed at the acting branch only, so the write actions wait while
// another branch is in view. Keyboard: / focuses the search, ↑↓ move, Enter selects, Esc clears the search.
import type { ProductCategory, ProductSummary, ProductTab, SerialSummary, StockLineView, StockState, StockSummary } from "@visionx/contracts";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router";
import { serials, stock, type StockFilters } from "../api/endpoints.inventory";
import { isApiError } from "../api/problem";
import { formatCount, formatDate, formatDayMonth, formatMoney, formatTime } from "../lib/format";
import { useOneStore } from "../session/one-store";
import { can, useSessionView } from "../session/store";
import { Alert, Checkbox, Chip, Empty, ErrorAlert, Field, KV, Loading, LockMark, Modal, Pager, Panel, Select, TextInput } from "../ui/bits";
import { Icon } from "../ui/icons";
import { ProductPicker, TAB_CATEGORIES, TAB_LABEL, andList } from "./CatalogueShared";
import { NoAccessPanel, PageHead } from "./common";
import { useDebounce } from "./PatientsShared";
import { AdjustModal } from "./StockAdjust";
import {
  DocumentRef, MovementItem, ReOrderChip, STATE_WORD, SerialStatusChip, groupMovements, isToday, shortName, signed, useStockBranch, useStockWrites, useTransferOffered,
} from "./StockShared";

const PAGE = 50;
/** UC-INVENTORY-001 step 4: the state chips — lines holding units in that state; Damaged joins them once a line holds one (A6). */
const STATE_CHIPS: readonly StockState[] = ["InStore", "Reserved", "AtLab", "InTransit"];
/** The categories a stock line can be of (a service holds none, BR-INVENTORY-001). */
const STOCK_TABS: readonly Exclude<ProductTab, "services">[] = ["frames", "lenses", "contactLenses", "accessories"];
const isStockTab = (v: string | null): v is Exclude<ProductTab, "services"> => STOCK_TABS.some((t) => t === v);
const isState = (v: string | null): v is StockState => v === "InStore" || v === "Reserved" || v === "AtLab" || v === "Damaged" || v === "InTransit";
const isTyping = (t: EventTarget | null): boolean => t instanceof HTMLElement && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable);
const isControl = (t: EventTarget | null): boolean => t instanceof HTMLElement && t.closest("a[href], button, [role='button'], [role='checkbox'], [role='option']") !== null;

/** What another stock screen hands the list when it opens it: a notice (a serial deleted). */
export interface StockState2 { notice?: string | undefined }

export function Stock() {
  const s = useSessionView();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { online, title } = useStockWrites();
  const br = useStockBranch();
  // A practice of one store: no transfer while the store has none, and nothing said of a receiving branch (UC-INVENTORY-001 A8,
  // UC-INVENTORY-011 A12).
  const oneStore = useOneStore();
  const transferOffered = useTransferOffered();
  const [sp, setSp] = useSearchParams();
  const state = isState(sp.get("state")) ? (sp.get("state") as StockState) : undefined;
  const below = sp.get("below") === "true";
  const tab = isStockTab(sp.get("category")) ? (sp.get("category") as Exclude<ProductTab, "services">) : undefined;
  const selected = sp.get("product");
  // With All branches in view a product has a line at each branch: the selection names the branch too (`at`), so a line chosen
  // at Centurion shows Centurion's figures, serials and movements (the review of 18 Oct 2026, D1-6; TC-INVENTORY-147).
  const selectedAt = sp.get("at");
  const [typed, setTyped] = useState(sp.get("q") ?? "");
  const [q, flush] = useDebounce(typed.trim(), 300);
  useEffect(() => {
    if ((sp.get("q") ?? "") === q) return;
    setSp((p) => { const n = new URLSearchParams(p); if (q) n.set("q", q); else n.delete("q"); return n; }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);
  const canRead = can(s, "inventory.stock.read");
  const filters: StockFilters = { branchId: br.branchId ?? undefined, state, belowReOrder: below, category: tab ? (TAB_CATEGORIES[tab] as ProductCategory[]) : undefined, q: q || undefined };
  const filterKey = JSON.stringify(filters);
  const [cursors, setCursors] = useState<string[]>([]);
  const cursor = cursors[cursors.length - 1];
  useEffect(() => { setCursors([]); }, [filterKey]);
  const list = useQuery({
    queryKey: ["stock", "list", filters, cursor ?? ""],
    queryFn: () => stock.list(filters, { limit: PAGE, cursor, count: cursor === undefined }),
    placeholderData: keepPreviousData, enabled: canRead && br.branchId !== null,
  });
  const summary = useQuery({ queryKey: ["stock", "summary", br.branchId], queryFn: () => stock.summary(br.branchId ?? undefined), enabled: canRead && br.branchId !== null });
  const [total, setTotal] = useState<number | undefined>(undefined);
  useEffect(() => { if (list.data?.data.totalCount !== undefined) setTotal(list.data.data.totalCount); }, [list.data]);
  const rows = list.data?.data.items ?? [];
  // A search by serial number finds one line and marks the serial in its list (A2): the line is selected for it.
  const matched = rows.length === 1 && rows[0]!.matchedSerial ? rows[0]! : null;
  const isSelected = (r: StockLineView): boolean => r.productId === selected && (!br.isAll || selectedAt === null || r.branchId === selectedAt);
  const line = rows.find(isSelected) ?? matched ?? null;
  const setParam = (changes: Record<string, string | null>) =>
    setSp((p) => { const n = new URLSearchParams(p); for (const [k, v] of Object.entries(changes)) { if (v) n.set(k, v); else n.delete(k); } return n; }, { replace: true });
  const select = (r: StockLineView) => setParam({ product: r.productId, at: br.isAll ? r.branchId : null });

  const passed = (location.state ?? null) as StockState2 | null;
  const [notice, setNotice] = useState<ReactNode>(null);
  useEffect(() => {
    if (!passed?.notice) return;
    setNotice(passed.notice);
    navigate(location.pathname + location.search, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passed]);
  // A re-order level for a line, or for a product the branch has never held, which has none (UC-INVENTORY-002 A2): `line: null`.
  const [dialog, setDialog] = useState<{ kind: "adjust"; line: StockLineView | null } | { kind: "reorder"; line: StockLineView | null } | null>(null);

  // The keyboard's row (A8 of the catalogue's list, as Products has it): the selected line while it is on the page, else the first.
  const [hi, setHi] = useState(0);
  const arrowed = useRef(false);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => { const at = rows.findIndex(isSelected); setHi(at >= 0 ? at : 0); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [list.data, selected, selectedAt]);
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const hiRef = useRef(hi);
  hiRef.current = hi;
  // The handler below is registered once: it reaches this render's select and flush (and with them the address as it is now).
  const selectRef = useRef(select);
  selectRef.current = select;
  const flushRef = useRef(flush);
  flushRef.current = flush;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey || document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      const typing = isTyping(e.target);
      const inBox = e.target === input.current;
      if (e.key === "/" && !typing) { e.preventDefault(); e.stopPropagation(); input.current?.focus(); input.current?.select(); return; }
      if (typing && !inBox) return;
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        if (!inBox && isControl(e.target)) return;
        e.preventDefault();
        arrowed.current = true;
        setHi((i) => Math.min(Math.max(i + (e.key === "ArrowDown" ? 1 : -1), 0), Math.max(rowsRef.current.length - 1, 0)));
      } else if (e.key === "Enter") {
        // A code and Enter — a scanner's, or a search typed — searches at once; after the arrows, Enter selects the highlighted line.
        if (inBox && !arrowed.current) { e.preventDefault(); flushRef.current(); return; }
        if (!inBox && isControl(e.target)) return;
        const r = rowsRef.current[hiRef.current];
        if (r) { e.preventDefault(); selectRef.current(r); }
      } else if (e.key === "Escape" && inBox) { e.preventDefault(); setTyped(""); }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!canRead) return <section className="screen" id="s-stock"><div className="content"><PageHead title="Stock" /><NoAccessPanel page="Stock" permission="inventory.stock.read" /></div></section>;
  const denied = list.isError && isApiError(list.error) && list.error.code === "PERMISSION_DENIED";
  const canAdjust = can(s, "inventory.adjustment.write");
  const takes = can(s, "inventory.stock_take.read");
  const writeTitle = !online ? title : !br.isActing ? `Stock is changed at your acting branch${br.actingName ? `, ${br.actingName}` : ""}` : undefined;
  const writable = online && br.isActing;
  // UC-INVENTORY-002 A2: a product the branch has never held has no line to choose; a stock role gives it a level from here.
  const canLevel = can(s, "inventory.stock.write") && writable;
  const levelFor = (label: string) => <button className="btn" type="button" onClick={() => setDialog({ kind: "reorder", line: null })}>{label}</button>;
  const damaged = rows.some((r) => r.damaged > 0);
  const totals = list.data?.data.totals;
  const sum = summary.data?.data;
  const first = cursors.length * PAGE + 1;
  const hasFilters = typed !== "" || state !== undefined || below || tab !== undefined;
  const where = br.isAll ? "every branch" : br.name;

  return (
    <section className="screen" id="s-stock">
      <div className="content">
        <PageHead
          title={`Stock — ${br.name || "Stock"}`}
          sub="Frames, lenses, contact lenses and accessories · serial-tracked frames · valued at weighted average cost"
          actions={<>
            <Link className="btn" to="/products">Products</Link>
            {can(s, "inventory.stock_take.read") && <Link className="btn" to="/stock-take"><Icon name="barcode" />Stock take</Link>}
            {canAdjust && <button className="btn" type="button" disabled={!writable} title={writeTitle} onClick={() => setDialog({ kind: "adjust", line: null })}>Adjust<LockMark /></button>}
            {transferOffered && <Link className="btn" to="/stock-transfer">Transfer</Link>}
            {can(s, "purchasing.po.write") && <Link className="btn primary" to="/stock/receive"><Icon name="plus" />Receive delivery</Link>}
          </>}
        />
        {notice && <Alert tone="ok" role="status" actions={<button className="btn sm" type="button" onClick={() => setNotice(null)}>Dismiss</button>}>{notice}</Alert>}
        {!br.isActing && br.actingName && (
          <Alert tone="info">Viewing {br.isAll ? "all branches" : br.name}. Stock is changed at your acting branch, {br.actingName}: switch branch in the top bar to adjust, count or transfer {br.isAll ? "another branch's" : `${br.name}'s`} stock.</Alert>
        )}
        {sum && <SummaryStrip sum={sum} />}
        {sum && br.isAll && sum.branches.length > 0 && (
          <div className="small faint">{sum.branches.map((b) => `${b.branchName}: ${formatMoney(b.valueCents)} over ${formatCount(b.lines)} line${b.lines === 1 ? "" : "s"}`).join(" · ")}</div>
        )}
        <div className="toolbar">
          <div className="input w-search">
            <Icon name="search" />
            <input ref={input} type="search" aria-label="Search stock" placeholder="Search SKU, product, serial or barcode" autoComplete="off" maxLength={100} value={typed} onChange={(e) => { setTyped(e.target.value); arrowed.current = false; }} />
            <kbd>/</kbd>
          </div>
          <div className="chips" role="group" aria-label="State">
            <StateChip on={state === undefined} label="All" onClick={() => setParam({ state: null })} />
            {[...STATE_CHIPS, ...(sum && sum.damaged > 0 ? ["Damaged" as const] : [])].map((st) => (
              <StateChip key={st} on={state === st} label={STATE_WORD[st]} onClick={() => setParam({ state: state === st ? null : st })} />
            ))}
          </div>
          <Select aria-label="Category" boxClass="w-cat" value={tab ?? ""} onChange={(e) => setParam({ category: e.target.value || null })}>
            <option value="">Category · All</option>
            {STOCK_TABS.map((t) => <option key={t} value={t}>Category · {TAB_LABEL[t]}</option>)}
          </Select>
          <Checkbox checked={below} onChange={(v) => setParam({ below: v ? "true" : null })}>Below re-order only</Checkbox>
          {br.choices.length > 1 && (
            <Select aria-label="Branch" boxClass="w-branch" value={br.branchId ?? ""} onChange={(e) => br.choose(e.target.value)}>
              {br.choices.map((b) => <option key={b.id} value={b.id}>Branch · {b.name}</option>)}
            </Select>
          )}
          <span className="right small faint" aria-live="polite">{list.data ? (total !== undefined ? `${cursors.length === 0 ? rows.length : `${first}–${first + rows.length - 1}`} of ${formatCount(total)} line${total === 1 ? "" : "s"}` : `${rows.length} shown`) : ""}</span>
        </div>
        {list.isPending && <Loading what="stock" />}
        {list.isError && (denied ? <NoAccessPanel page="Stock" error={list.error} /> : <ErrorAlert error={list.error} />)}
        {list.data && (
          <Panel tight foot={<>
            <span className="help">
              On hand = in store + reserved + at lab{damaged ? " + damaged" : ""} + in transit.{!oneStore && <> A unit on a sent transfer stays in {br.isAll ? "the sending branch" : br.name}&#39;s on hand, In transit, until the receiving branch scans it in.</>}
              {canLevel && rows.length > 0 && <> A product {br.name} has never held has no line: <button className="linkbtn" type="button" onClick={() => setDialog({ kind: "reorder", line: null })}>set its re-order level</button>.</>}
            </span>
            {(cursors.length > 0 || list.data.data.nextCursor) && <Pager label={`Page ${cursors.length + 1}`} hasPrev={cursors.length > 0} hasNext={!!list.data.data.nextCursor} onPrev={() => setCursors((c) => c.slice(0, -1))} onNext={() => setCursors((c) => [...c, list.data.data.nextCursor!])} />}
          </>}>
            {rows.length === 0 ? (
              <Empty title={q ? `No stock line matches ‘${q}’.` : "No stock line matches these filters."} actions={hasFilters || canLevel ? <>
                {hasFilters && <button className="btn" type="button" onClick={() => { setTyped(""); setSp(br.isActing ? new URLSearchParams() : new URLSearchParams({ branch: br.branchId ?? "" }), { replace: true }); }}>Clear filters</button>}
                {canLevel && levelFor("Set a re-order level…")}
              </> : undefined}>
                {q ? "A search matches an SKU, a product's name, a serial number or a barcode." : "A product has a stock line once its stock has moved at the branch or a re-order level was set for it."}
                {canLevel && ` A product ${br.name} has never held has no line until it is given a re-order level.`}
              </Empty>
            ) : (
              <div className="tbl-scroll">
                <table>
                  <thead><tr>
                    <th className="sku-col">SKU</th>{br.isAll && <th>Branch</th>}<th>Product</th><th className="r">On hand</th><th className="r">In store</th><th className="r state-col">Reserved</th><th className="r state-col">At lab</th>
                    {damaged && <th className="r state-col">Damaged</th>}<th className="r state-col">In transit</th><th className="r">Re-order level</th><th className="r">Wtd avg cost</th><th className="r">Value</th><th className="wide-only">Last received</th>
                  </tr></thead>
                  <tbody>
                    {rows.map((r, i) => <LineRow key={`${r.branchId}-${r.productId}`} r={r} index={i} highlighted={i === hi} selected={line?.productId === r.productId && line.branchId === r.branchId} showBranch={br.isAll} damaged={damaged} takes={takes} onSelect={() => select(r)} />)}
                  </tbody>
                  {totals && (
                    <tfoot><tr>
                      <td className="sku-col"></td><td colSpan={br.isAll ? 2 : 1}>Lines shown</td><td className="r num">{formatCount(totals.onHand)}</td><td className="r num">{formatCount(totals.inStore)}</td><td className="r num state-col">{formatCount(totals.reserved)}</td>
                      <td className="r num state-col">{formatCount(totals.atLab)}</td>{damaged && <td className="r num state-col">{formatCount(totals.damaged)}</td>}<td className="r num state-col">{formatCount(totals.inTransit)}</td><td></td><td></td>
                      <td className="money">{formatMoney(totals.valueCents)}</td><td className="wide-only"></td>
                    </tr></tfoot>
                  )}
                </table>
              </div>
            )}
          </Panel>
        )}
        {line && (
          <div className="split wide stock-line">
            {line.trackSerials
              ? <SerialsPanel line={line} actions={<LineActions line={line} writable={writable} title={writeTitle} onReorder={() => setDialog({ kind: "reorder", line })} onAdjust={() => setDialog({ kind: "adjust", line })} />} />
              : <LinePanel line={line} takes={takes} actions={<LineActions line={line} writable={writable} title={writeTitle} onReorder={() => setDialog({ kind: "reorder", line })} onAdjust={() => setDialog({ kind: "adjust", line })} />} />}
            <MovementsPanel branchId={br.isAll ? line.branchId : br.branchId} branchName={br.isAll ? line.branchName : where} productId={line.productId} sku={line.sku} />
          </div>
        )}
        {!line && list.data && rows.length > 0 && <MovementsPanel branchId={br.branchId} branchName={where} showBranch={br.isAll} />}
        <div className="kbd-hints">
          <span><kbd>↑</kbd><kbd>↓</kbd> move</span>
          <span><kbd>Enter</kbd> select a line · after a code, search</span>
          <span><kbd>/</kbd> search</span>
          <span><kbd>Esc</kbd> clear search</span>
        </div>
        {dialog?.kind === "reorder" && (
          <ReOrderModal line={dialog.line} branch={br.name} onClose={() => setDialog(null)} onDone={(l) => {
            setDialog(null);
            void qc.invalidateQueries({ queryKey: ["stock"] });
            setNotice(<><b>Re-order level for <span className="mono">{l.sku}</span> at {l.branchName} is {l.reOrderLevel}.</b> {l.reOrderLevel === 0 ? "The line is not monitored." : l.belowReOrder ? `${l.available} available is at or below it: flagged Re-order.` : `${l.available} available is above it.`}</>);
          }} />
        )}
        {dialog?.kind === "adjust" && (
          <AdjustModal initial={dialog.line} onClose={() => setDialog(null)} onDone={(a) => {
            setDialog(null);
            void qc.invalidateQueries({ queryKey: ["stock"] });
            void qc.invalidateQueries({ queryKey: ["serials"] });
            setNotice(a
              ? <><b><Link className="mono" to={`/stock/adjustments/${a.id}`}>{a.number}</Link> posted.</b> {a.lineCount} line{a.lineCount === 1 ? "" : "s"} · {formatMoney(a.totalValueCents)} at cost{a.authorisedBy ? ` · authorised by ${shortName(a.authorisedBy.displayName)}` : ""}.</>
              : <><b>The adjustment was posted: its answer did not arrive.</b> It is listed under Adjustments.</>);
          }} />
        )}
      </div>
    </section>
  );
}

function StateChip({ on, label, onClick }: { on: boolean; label: string; onClick: () => void }) {
  return <button type="button" className={`chip ${on ? "accent" : "neutral off"}`} aria-pressed={on} onClick={onClick}>{label}</button>;
}

/** UC-INVENTORY-001 step 3: the branch's value, frames by state, the oldest frame at a lab, the lines below re-order. */
function SummaryStrip({ sum }: { sum: StockSummary }) {
  const others = sum.belowReOrder.otherNames;
  const plus = others.length === 0 ? null : others.length <= 2 ? `plus ${andList(others)}` : `plus ${others.length} other lines`;
  return (
    <div className="stats">
      <div className="stat"><span className="v">{formatMoney(sum.valueCents)}</span><span className="l">Stock value (wtd avg)</span><span className="d">{formatCount(sum.lines)} line{sum.lines === 1 ? "" : "s"}</span></div>
      <div className="stat"><span className="v">{formatCount(sum.frames.onHand)}</span><span className="l">Frames on hand</span><span className="d">{formatCount(sum.frames.inStore)} in store</span></div>
      <div className="stat"><span className="v">{formatCount(sum.atLab)}</span><span className="l">At lab</span><span className="d">{sum.oldestAtLabSentAt ? `oldest sent ${formatDayMonth(sum.oldestAtLabSentAt)}` : "none at a lab"}</span></div>
      <div className="stat"><span className="v">{formatCount(sum.frames.reserved)}</span><span className="l">Reserved</span><span className="d">frames on open orders</span></div>
      <div className="stat"><span className="v">{formatCount(sum.belowReOrder.frames)}</span><span className="l">Frames below re-order</span><span className={`d${sum.belowReOrder.frames + sum.belowReOrder.other > 0 ? " down" : ""}`}>{plus ?? (sum.belowReOrder.frames > 0 ? "frames only" : "nothing below its level")}</span></div>
    </div>
  );
}

/**
 * What holds a line's units apart from the store, and when it was last counted (UC-INVENTORY-001 step 2's sub-line). The count is
 * linked for the roles that open stock takes (`takes`); a Receptionist reads its number (TC-INVENTORY-150).
 */
function holdsLine(r: StockLineView, takes: boolean): ReactNode[] {
  const parts: ReactNode[] = [];
  for (const h of r.holds) {
    const doc = <DocumentRef doc={h.document} />;
    if (h.kind === "Reserved") parts.push(<>{h.units} reserved{h.document?.number ? <> · {doc}</> : null}</>);
    else if (h.kind === "AtLab") parts.push(<>{h.units} at lab{h.document?.number ? <> · {doc}</> : null}</>);
    else parts.push(<>{h.units} {h.status === "Draft" ? "on draft" : "in transit on"} {doc}</>);
  }
  if (r.damaged > 0) parts.push(<>{r.damaged} damaged</>);
  if (r.lastCounted) parts.push(<>last counted {formatDate(r.lastCounted.on)} · {takes ? <Link to={`/stock-take?take=${r.lastCounted.stockTakeId}`}>{r.lastCounted.stockTakeNumber}</Link> : r.lastCounted.stockTakeNumber} · variance {signed(r.lastCounted.varianceQty)}</>);
  if (r.matchedSerial) parts.push(<>serial <span className="mono">{r.matchedSerial}</span></>);
  return parts;
}

function LineRow({ r, index, highlighted, selected, showBranch, damaged, takes, onSelect }: { r: StockLineView; index: number; highlighted: boolean; selected: boolean; showBranch: boolean; damaged: boolean; takes: boolean; onSelect: () => void }) {
  const sub = holdsLine(r, takes);
  const chips = r.belowReOrder || r.inactive;
  return (
    <tr data-row={index} className={`clickable${selected || highlighted ? " sel" : ""}`} aria-selected={selected} onClick={onSelect}>
      <td className="mono nw sku-col"><Link to={`/products/${r.productId}`} onClick={(e) => e.stopPropagation()}>{r.sku}</Link></td>
      {showBranch && <td className="nw">{r.branchName}</td>}
      <td>
        {chips ? <div className="row c gap-8"><span>{r.name}</span>{r.belowReOrder && <ReOrderChip />}{r.inactive && <Chip tone="neutral">Inactive</Chip>}</div> : r.name}
        <div className="small faint mono sku-sub">{r.sku}</div>
        {sub.length > 0 && <div className="small faint">{sub.map((p, i) => <span key={i}>{i > 0 ? " · " : ""}{p}</span>)}</div>}
      </td>
      <td className="r num strong">{formatCount(r.onHand)}</td>
      <td className="r num">{formatCount(r.inStore)}</td>
      <td className="r num state-col">{formatCount(r.reserved)}</td>
      <td className="r num state-col">{formatCount(r.atLab)}</td>
      {damaged && <td className="r num state-col">{formatCount(r.damaged)}</td>}
      <td className="r num state-col">{formatCount(r.inTransit)}</td>
      <td className="r num">{r.reOrderLevel === 0 ? <span className="faint">—</span> : formatCount(r.reOrderLevel)}</td>
      <td className="money">{formatMoney(r.averageCostCents)}</td>
      <td className="money">{formatMoney(r.valueCents)}</td>
      <td className="nw wide-only">{r.lastReceivedOn ? formatDate(r.lastReceivedOn) : <span className="faint">—</span>}</td>
    </tr>
  );
}

function LineActions({ line, writable, title, onReorder, onAdjust }: { line: StockLineView; writable: boolean; title: string | undefined; onReorder: () => void; onAdjust: () => void }) {
  const s = useSessionView();
  return (
    <span className="right row c gap-8">
      {can(s, "inventory.stock.write") && <button className="btn sm" type="button" disabled={!writable} title={title} onClick={onReorder}>Re-order level</button>}
      {can(s, "inventory.adjustment.write") && <button className="btn sm" type="button" disabled={!writable} title={title} aria-label={`Adjust ${line.sku}`} onClick={onAdjust}>Adjust<LockMark /></button>}
    </span>
  );
}

/** UC-INVENTORY-004 A8: the product's serials at the branch, sold and written-off ones included. */
function SerialsPanel({ line, actions }: { line: StockLineView; actions: ReactNode }) {
  const q = useQuery({ queryKey: ["serials", "of", line.productId, line.branchId], queryFn: () => serials.list({ productId: line.productId, branchId: line.branchId }, { limit: 200 }) });
  const items = q.data?.data.items ?? [];
  const onHand = q.data?.data.onHandCount ?? 0;
  const dates = new Set(items.map((x) => x.receivedOn));
  const costs = new Set(items.map((x) => x.unitCostCents));
  const sameArrival = items.length > 1 && dates.size === 1 && costs.size === 1 && items[0]!.receivedOn && items[0]!.unitCostCents !== null;
  return (
    <Panel
      title={`Serials — ${line.name}`}
      meta={<><span className="mono">{line.sku}</span> · {items.length} serial{items.length === 1 ? "" : "s"} · {onHand} on hand{line.branchName ? ` · ${line.branchName}` : ""}</>}
      head={actions}
      tight
      foot={q.data ? <span className="small faint">Sold and written-off serials stay on record.{sameArrival ? ` All ${items.length} units came in on ${formatDate(items[0]!.receivedOn)} at ${formatMoney(items[0]!.unitCostCents)}, so the weighted average is ${formatMoney(line.averageCostCents)}.` : ` ${line.branchName} values the product at its weighted average, ${formatMoney(line.averageCostCents)}.`}</span> : undefined}
    >
      {q.isPending && <Loading what="serials" />}
      {q.isError && <ErrorAlert error={q.error} />}
      {q.data && (items.length === 0 ? <Empty title="No serials at this branch." /> : (
        <div className="tbl-scroll">
          <table>
            <thead><tr><th>Serial</th><th>Location</th><th>Status</th><th>Received</th><th className="r">Cost</th></tr></thead>
            <tbody>{items.map((x) => <SerialRow key={x.id} x={x} marked={x.serialNumber === line.matchedSerial} />)}</tbody>
          </table>
        </div>
      ))}
    </Panel>
  );
}

function SerialRow({ x, marked }: { x: SerialSummary; marked: boolean }) {
  const detail: ReactNode = x.status === "Sold" ? <>{x.soldInvoiceId && <span className="mono">{x.soldInvoiceId}</span>}{x.soldAt && <span className="small faint">{formatDate(x.soldAt)}</span>}</>
    : x.status === "AtLab" ? <>{x.labOrderId && <span className="mono">{x.labOrderId}</span>}<span className="small faint">{[x.reservedOrderId ? `for ${x.reservedOrderId}` : null, x.sentToLabAt ? `sent ${isToday(x.sentToLabAt) ? formatTime(x.sentToLabAt) : formatDate(x.sentToLabAt)}` : null, x.daysAtLab !== null && x.daysAtLab > 0 ? `${x.daysAtLab} days` : null].filter(Boolean).join(" · ")}</span></>
      : x.status === "Reserved" ? <>{x.reservedOrderId && <span className="mono">{x.reservedOrderId}</span>}</>
        : x.status === "WrittenOff" || x.status === "Damaged" || x.status === "Returned" ? <span className="small faint">{[x.statusChangedAt ? formatDate(x.statusChangedAt) : null, x.statusReason ? x.statusReason === "StockTakeVariance" ? "stock-take variance" : x.statusReason.replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase() : null].filter(Boolean).join(" · ")}</span>
          : null;
  return (
    <tr className={marked ? "sel" : undefined} aria-selected={marked || undefined}>
      <td className="mono nw"><Link to={`/stock/serials/${x.id}`}>{x.serialNumber}</Link>{marked && <> <Chip tone="accent">matched</Chip></>}</td>
      <td>{x.location === "None" ? <span className="faint">—</span> : STATE_WORD[x.location]}</td>
      <td><div className="row c gap-8"><SerialStatusChip status={x.status} />{detail}</div></td>
      <td className="nw">{x.receivedOn ? formatDate(x.receivedOn) : <span className="faint">—</span>}</td>
      <td className="money">{formatMoney(x.unitCostCents)}</td>
    </tr>
  );
}

/** A line held without serials: its figures and what holds its units. */
function LinePanel({ line, takes, actions }: { line: StockLineView; takes: boolean; actions: ReactNode }) {
  const sub = holdsLine(line, takes);
  const away = ([[line.reserved, "reserved"], [line.atLab, "at lab"], [line.damaged, "damaged"], [line.inTransit, "in transit"]] as const).filter(([n]) => n > 0).map(([n, w]) => `${n} ${w}`);
  return (
    <Panel title={line.name} meta={<><span className="mono">{line.sku}</span> · {line.branchName}</>} head={actions}>
      <KV rows={[
        ["On hand", <>{formatCount(line.onHand)}{away.length > 0 ? ` · ${line.inStore} in store, ${away.join(", ")}` : ""}</>],
        ["Available", formatCount(line.available)],
        ["Re-order level", line.reOrderLevel === 0 ? <span className="faint">Not monitored</span> : <div className="row c gap-8"><span>{line.reOrderLevel}</span>{line.belowReOrder && <ReOrderChip />}</div>],
        ["Wtd avg cost", <span className="mono">{formatMoney(line.averageCostCents)}</span>],
        ["Value", <span className="mono">{formatMoney(line.valueCents)}</span>],
        ["Last received", line.lastReceivedOn ? formatDate(line.lastReceivedOn) : <span className="faint">—</span>],
        ...(sub.length > 0 ? [["Held", <>{sub.map((p, i) => <span key={i}>{i > 0 ? " · " : ""}{p}</span>)}</>] as [ReactNode, ReactNode]] : []),
      ]} />
    </Panel>
  );
}

/** UC-INVENTORY-003: the latest movements of the branch, or of the selected product there; All movements opens the ledger. */
function MovementsPanel({ branchId, branchName, productId, sku, showBranch }: { branchId: string | null; branchName: string; productId?: string | undefined; sku?: string | undefined; showBranch?: boolean | undefined }) {
  const s = useSessionView();
  const q = useQuery({ queryKey: ["stock", "movements", "panel", branchId, productId ?? ""], queryFn: () => stock.movements({ branchId: branchId ?? undefined, productId }, { limit: 20 }), enabled: branchId !== null });
  const groups = groupMovements(q.data?.data.items ?? []);
  const params = new URLSearchParams();
  if (branchId) params.set("branch", branchId);
  if (productId) params.set("product", productId);
  return (
    <Panel title="Movements" meta={<>{sku ? <><span className="mono">{sku}</span> at </> : null}{branchName} · newest first</>} head={<span className="right row c gap-8">
      {can(s, "inventory.adjustment.write") && <Link className="btn sm" to={`/stock/adjustments${branchId ? `?branch=${branchId}` : ""}`}>Adjustments</Link>}
      <Link className="btn sm" to={`/stock/movements?${params.toString()}`}>All movements</Link>
    </span>}>
      {q.isPending && <Loading what="movements" />}
      {q.isError && <ErrorAlert error={q.error} />}
      {q.data && (groups.length === 0 ? <Empty title="No movements yet." /> : <div className="list">{groups.map((g) => <MovementItem key={g[0]!.id} group={g} showBranch={showBranch} />)}</div>)}
    </Panel>
  );
}

/**
 * UC-INVENTORY-002: a whole number 0–9 999 at the acting branch; 0 means not monitored. Enter saves. With no line (A2) the product
 * is picked first — a stocked product, never a composite — and the branch's line is made with the level.
 */
function ReOrderModal({ line, branch, onClose, onDone }: { line: StockLineView | null; branch: string; onClose: () => void; onDone: (l: StockLineView) => void }) {
  const [product, setProduct] = useState<ProductSummary | null>(null);
  const [text, setText] = useState(line ? String(line.reOrderLevel) : "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [productError, setProductError] = useState<string | null>(null);
  const [failure, setFailure] = useState<unknown>(null);
  const words = "A whole number from 0 to 9 999";
  const productId = line?.productId ?? product?.id ?? null;
  const submit = async () => {
    if (busy) return;
    if (productId === null) { setProductError("Choose the product"); return; }
    const t = text.trim();
    const n = Number(t);
    if (t === "" || !Number.isFinite(n)) { setError(words); return; }
    setBusy(true);
    setError(null);
    setFailure(null);
    try { onDone((await stock.setReOrderLevel(productId, n)).data); }
    catch (err) {
      if (isApiError(err) && err.field("level")) setError(words);
      else if (isApiError(err) && err.field("productId")?.code === "not_stocked") setProductError("This product holds no stock: a service, a made-to-order lens or a composite has no re-order level");
      else setFailure(err);
    } finally { setBusy(false); }
  };
  return (
    <Modal title={line ? `Re-order level — ${line.sku}` : "Re-order level"} meta={line ? `${line.name} · ${branch}` : `${branch} · a product with no line here`} onClose={onClose} onSubmit={() => void submit()} footer={<>
      <button className="btn" type="button" onClick={onClose}>Cancel</button>
      <button className="btn primary" type="submit" disabled={busy}>{busy ? "Saving…" : "Save"}</button>
    </>}>
      {failure !== null && <ErrorAlert error={failure} />}
      {!line && (
        <ProductPicker label="Product" required value={product} onPick={(p) => { setProduct(p); setProductError(null); }} keyTag="reorder-level" allow={(p) => p.stocked && !p.isComposite}
          error={productError ?? undefined} help={`Its line at ${branch} is made with the level, with nothing on hand`} />
      )}
      <Field label="Re-order level" htmlFor="reorder-level" error={error ?? undefined} help={`0 is not monitored · flagged Re-order when available is at or below it${line ? ` · ${line.available} available now` : ""}`}>
        <TextInput id="reorder-level" inputMode="numeric" value={text} error={error !== null} onChange={(e) => { setText(e.target.value); setError(null); }} boxClass="w-120" autoFocus={line !== null} />
      </Field>
    </Modal>
  );
}
